import { type CheckoutSession, type Store } from "@/types";
import { browserAgent } from "@/shared/telemetry/newrelic";

const THREEDS_RETURN_LOGGED_KEY_PREFIX = "nr_threeds_return_logged_";
const THREEDS_SESSION_KEY_PREFIX = "nr_3ds_checkout_";
const SUCCESS_SCREEN_LOGGED_KEY_PREFIX = "nr_success_screen_logged_";

const resultToTelemetry = (result: Result): string => {
  if (result === Result.OK) return "success";
  if (result === Result.FAILED) return "failed";
  return "unknown";
};

const normalizePath = (pathname: string): string =>
  pathname.replace(/^\/v2/, "");

const returnLoggedKey = (checkoutId: string): string =>
  `${THREEDS_RETURN_LOGGED_KEY_PREFIX}${checkoutId}`;

const threedsSessionKey = (checkoutId: string): string =>
  `${THREEDS_SESSION_KEY_PREFIX}${checkoutId}`;

export const successScreenLoggedKey = (checkoutId: string): string =>
  `${SUCCESS_SCREEN_LOGGED_KEY_PREFIX}${checkoutId}`;

export enum Result {
  NONE,
  OK,
  FAILED,
}

const decodeResult = (resultEncoded: string): Result => {
  if (resultEncoded === "ok") return Result.OK;

  if (resultEncoded === "failed") return Result.FAILED;

  return Result.NONE;
};

const encodeResult = (result: Result): string => {
  if (result === Result.OK) return "ok";

  if (result === Result.FAILED) return "failed";

  return "none";
};

/**
 * Handle redirection from bank after 3DS verification.
 * Detects success/failure from the URL path and stores the result.
 *
 * Only writes to the store when a meaningful result (OK or FAILED) is detected.
 * Writing NONE would overwrite a previously set result, which causes race
 * conditions with the poll function and leads to infinite redirect loops
 * between /3ds and /confirmation.
 */
export const handleRedirection = async (window: Window, store: Store) => {
  const pathname = window.location.pathname;

  const lastRouteWord = pathname.split("/").slice(-1)[0] ?? "";

  let result = Result.NONE;
  if (lastRouteWord === "success") {
    result = Result.OK;
  }
  if (["failed", "fail", "failure"].includes(lastRouteWord)) {
    result = Result.FAILED;
  }

  if (result === Result.NONE) {
    return;
  }

  const resultEncoded = encodeResult(result);
  await store.set("3ds_result", resultEncoded);

  if (result === Result.OK) {
    await set3dsCompletedFlag(store);
  }
};

/**
 * Get the 3DS result from the store.
 */
export const get3dsResult = async (store: Store): Promise<Result> => {
  const resultEncoded = (await store.get("3ds_result")) ?? "";
  const result = decodeResult(resultEncoded);

  return result;
};

// ============================================
// Loop Prevention Functions
// ============================================

const THREEDS_COMPLETED_KEY = "3ds_completed";
const THREEDS_REDIRECT_INITIATED_KEY = "3ds_redirect_initiated";

/**
 * Set the 3DS completed flag in localStorage.
 * This flag is used to prevent redirect loops when returning from the bank.
 */
export const set3dsCompletedFlag = async (store: Store): Promise<void> => {
  await store.set(THREEDS_COMPLETED_KEY, "true");
};

/**
 * Check if 3DS has been marked as completed.
 * Used to prevent re-redirecting to the bank if the user has already completed 3DS.
 */
export const is3dsCompleted = async (store: Store): Promise<boolean> => {
  const value = await store.get(THREEDS_COMPLETED_KEY);
  return value === "true";
};

/**
 * Clear the 3DS completed flag.
 * This should be called when starting a new payment submission ("lazy cleanup").
 * Ensures each new payment attempt starts with a clean state.
 */
export const clear3dsCompletedFlag = async (store: Store): Promise<void> => {
  await store.set(THREEDS_COMPLETED_KEY, "");
};

/**
 * Clear all 3DS-related flags (both result and completed flag).
 * Useful for complete cleanup when starting a fresh payment.
 */
export const clear3dsState = async (store: Store): Promise<void> => {
  await store.set("3ds_result", "none");
  await store.set(THREEDS_COMPLETED_KEY, "");
  await store.set(THREEDS_REDIRECT_INITIATED_KEY, "");
};

// ============================================
// Redirect Initiation Tracking
// These functions track when a 3DS redirect has been initiated.
// This is critical for frictionless flows where the bank redirects
// back to any URL (not necessarily /3ds/success or /3ds/failed).
// ============================================

/**
 * Set the flag indicating that a 3DS redirect has been initiated.
 * This should be called BEFORE redirecting to the bank.
 * Used to detect when user returns from bank (regardless of return URL).
 */
export const set3dsRedirectInitiated = async (store: Store): Promise<void> => {
  await store.set(THREEDS_REDIRECT_INITIATED_KEY, "true");
};

/**
 * Check if a 3DS redirect has been initiated.
 * If true, the user has been sent to the bank and any subsequent
 * page load means they've returned (or are returning).
 */
export const is3dsRedirectInitiated = async (
  store: Store,
): Promise<boolean> => {
  const value = await store.get(THREEDS_REDIRECT_INITIATED_KEY);
  return value === "true";
};

/**
 * Clear the 3DS redirect initiated flag.
 * Called after processing the return from bank.
 */
export const clear3dsRedirectInitiated = async (
  store: Store,
): Promise<void> => {
  await store.set(THREEDS_REDIRECT_INITIATED_KEY, "");
};

/**
 * Clear sessionStorage flags related to 3DS that persist across payment attempts.
 * Should be called alongside clear3dsState when starting a new payment submission.
 */
export const clearSessionFlags = (checkoutId: string): void => {
  try {
    sessionStorage.removeItem(`reported_3ds_failure_${checkoutId}`);
    sessionStorage.removeItem(returnLoggedKey(checkoutId));
    sessionStorage.removeItem(threedsSessionKey(checkoutId));
    sessionStorage.removeItem(successScreenLoggedKey(checkoutId));
  } catch {
    // sessionStorage might be blocked
  }
};

/**
 * Marks that this checkout entered a 3DS bank redirect (survives store flag clears).
 * Used to tag downstream events (success screen, merchant redirect).
 */
export const mark3dsCheckoutSession = (checkoutId: string): void => {
  try {
    sessionStorage.setItem(threedsSessionKey(checkoutId), "true");
  } catch {
    // sessionStorage might be blocked
  }
};

/**
 * Whether this checkout was sent to the bank for 3DS during this attempt.
 */
export const had3dsCheckoutSession = (checkoutId: string): boolean => {
  try {
    return sessionStorage.getItem(threedsSessionKey(checkoutId)) === "true";
  } catch {
    return false;
  }
};

/**
 * Logs THREEDS_RETURN_FROM_BANK once per checkout on the first PPUI page load
 * after the user was sent to the bank. Centralized here so we capture returns
 * whether the user lands on /confirmation, /success, or /3ds/* — not only when
 * a specific poll branch runs before navigation to /success.
 */
export const logThreedsReturnFromBankIfNeeded = async (
  session: CheckoutSession,
  store: Store,
  pathname: string,
): Promise<void> => {
  const wasSentToBank = await is3dsRedirectInitiated(store);
  if (!wasSentToBank) {
    return;
  }

  try {
    if (sessionStorage.getItem(returnLoggedKey(session.id))) {
      return;
    }
    sessionStorage.setItem(returnLoggedKey(session.id), "true");
  } catch {
    // sessionStorage might be blocked — still attempt one log per call
  }

  const normalized = normalizePath(pathname);
  const result = await get3dsResult(store);

  browserAgent.addPageAction("THREEDS_RETURN_FROM_BANK", {
    checkout_id: session.id,
    checkout_status: session.status,
    return_path: pathname,
    flow: normalized.startsWith("/3ds/") ? "standard" : "frictionless",
    threed_result: resultToTelemetry(result),
  });
};
